import {
  ChangeDetectionStrategy, Component, ContentChild, ElementRef, EventEmitter, HostBinding, HostListener, Input, Output, TemplateRef, inject,
} from '@angular/core'
import {
  BooleanInput, coerceBooleanProperty,
} from '@angular/cdk/coercion'
import { CommonModule } from '@angular/common'

/**
 * Option IDs need to be unique across components, so this counter exists outside of
 * the component definition.
 */
let _uniqueIdCounter = 0

@Component({
  selector: 'kb-select-option',
  templateUrl: './select-option.component.html',
  styleUrls: ['./select-option.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule],
})
export class SelectOptionComponent<T> {
  @HostBinding('attr.role')
  public role = 'option'

  /** Whether or not the option is currently selected. */
  @Input()
  public get selected(): boolean { return this._selected }

  public set selected(value: BooleanInput) { this._selected = coerceBooleanProperty(value) }
  private _selected = false

  @Input()
  @HostBinding()
  public id = `kb-select-option-${_uniqueIdCounter++}`

  /** Event emitted when the option is selected or deselected. */
  @Output()
  public readonly selectedChange = new EventEmitter<SelectOptionComponent<T>>()

  /** The form value of the option. */
  @Input()
  public value?: T

  /** Whether the option is disabled. */
  @Input()
  @HostBinding('attr.aria-disabled')
  public get disabled(): boolean { return this._disabled }

  public set disabled(value: BooleanInput) { this._disabled = coerceBooleanProperty(value) }
  private _disabled = false

  @ContentChild(TemplateRef) public label!: TemplateRef<unknown>
  @ContentChild('selectedTemplate', { read: TemplateRef }) public selectedTemplate?: TemplateRef<unknown>

  private hostElement = inject(ElementRef)

  public getOffsetHeight(): number {
    return this.hostElement.nativeElement.offsetHeight
  }

  public getLabelText(): string {
    return this.hostElement.nativeElement.innerText
  }

  @HostListener('click')
  public select(): void {
    if (!this.disabled) {
      this._selected = true
      this.selectedChange.next(this)
    }
  }

  public deselect(): void {
    this._selected = false
    this.selectedChange.next(this)
  }

  /**
   * Gets the `aria-selected` value for the option. We explicitly omit the `aria-selected`
   * attribute from single-selection, unselected options. Including the `aria-selected="false"`
   * attributes adds a significant amount of noise to screen-reader users without providing useful
   * information.
   */
  @HostBinding('attr.aria-selected')
  protected get ariaSelected(): boolean {
    return this.selected
  }
}
